import * as React from 'react';
import {useCallback, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {Card, CardContent, CardHeader, Divider, Grid, IconButton, Stack, Typography} from '@mui/material';
import Button from '@mui/material/Button';
import {ArrowBack, PlaylistAddCheck} from '@mui/icons-material';
import {Formik, FormikErrors} from 'formik';
import {useAppDispatch} from '../../store';
import {useAppTranslation} from '../../services/i18n';
import {useMandatoryParams} from '../../services/currentSpace';
import {isApiResultError} from '../../helpers/api';
import {todayYmd} from '../../helpers/date';
import {addMessage} from 'store/localApp';
import {createApprovalRequest, fetchApprovalRequest, updateApprovalRequest} from 'store/approvals';
import {selectAuthUser} from 'store/selectors';
import {approvalJustification, approvalMissingFields, ApprovalRequestDetail, type ApprovalRequestType} from 'model/approval';
import {fromDateInput, isPriceValid, toDateInput, toPrice, toTimeInput, validateRequestTerm} from 'model/approvalForm';
import {TextFormField} from 'components/form/TextFormField';
import {CompetenceLevelFormField, type CompetenceLevelValue} from 'components/form/CompetenceLevelFormField';
import {ApprovalFileField, ApprovalFileValue, DRAFT_ACCEPTED_MIME_TYPES} from 'components/approval/ApprovalFileField';
import {ApprovalUserSelectField} from 'components/approval/ApprovalUserSelectField';
import {RequestModeToggle} from 'components/approval/RequestModeToggle';
import {UserAttributeCell} from 'components/approval/ApprovalReadOnly';
import {ReadOnlyField} from 'components/ReadOnlyField';

const COURSE_MODE: ApprovalRequestType = 'request_course';

/** `ApprovalRequest::MAX_FILES`; a request has no certificate, so all of it is attachments. */
const MAX_FILES = 10;

/** `subject_title` / `subject_content` length validation on the model. */
const MIN_TEXT_LENGTH = 3;

/** The heading of each half, as the design labels them. */
const sectionStyle = {
    fontSize: '11px',
    fontWeight: 500,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: 'var(--color-black-inactive)',
} as const;

/**
 * The design marks the half the manager owns in the space's own colour. The
 * darker mix, the one the chips use for text, keeps it readable on white.
 */
const managerSectionStyle = {...sectionStyle, color: 'var(--color-primary60)'} as const;

const hintStyle = {fontSize: '12px', color: 'var(--color-black-inactive)'} as const;

interface RequestFormValues {
    user_id?: number;
    request_type: ApprovalRequestType;
    subject_title: string;
    subject_content: string;
    reason: string;
    subject_provider: string;
    subject_date: string;
    subject_end_date: string;
    subject_start_time: string;
    subject_end_time: string;
    subject_location: string;
    price: string;
    note: string;
    competences: CompetenceLevelValue[];
    attachments: ApprovalFileValue[];
}

const emptyValues: RequestFormValues = {
    request_type: COURSE_MODE,
    subject_title: '', subject_content: '', reason: '', subject_provider: '',
    subject_date: '', subject_end_date: '', subject_start_time: '', subject_end_time: '',
    subject_location: '', price: '', note: '', competences: [], attachments: []
};

/**
 * The realization checklist over the form's own values, so the hint above the
 * fields names what is still missing while it is being typed. Shares
 * `approvalMissingFields` with the detail and the queue, so all three agree on
 * both the rule and the wording.
 */
const readinessMissing = (values: RequestFormValues): string[] => approvalMissingFields({
    subject_title: values.subject_title,
    subject_provider: values.subject_provider,
    price: values.price.trim() ? Number(values.price) : null,
    subject_date: values.subject_date || null,
    // a row without a competence picked counts as unlevelled either way
    competences: values.competences.map(c => ({competence_level: c.id ? c.competence_level : null})),
});

/**
 * A request for training: filed by the requester for themselves (`own`), filed
 * by a manager for a student, or worked up into something decidable once it
 * arrives.
 *
 * All three are one screen because they are one record at two ages, and the
 * design draws them as one layout -- what differs is who owns which half. While
 * it is being created the filer owns everything; after the first save only the
 * student and the mode are immutable (`Update::IMMUTABLE_ATTRIBUTES`), and what
 * remains is the course that answers the request -- plus the thematic topic,
 * which a manager may correct along the way.
 *
 * A manager's record may be saved incomplete on purpose -- sourcing a course
 * takes more than one sitting, and only `Schválit` insists on the full
 * checklist. What is still missing is named in the hint above the fields.
 */
export const ApprovalRequestForm = ({detail, own, onBack}: {
    detail?: ApprovalRequestDetail,
    /** the requester filing for themselves, from `#/dashboard/requests` */
    own?: boolean,
    onBack: () => void,
}) => {
    const t = useAppTranslation();
    const dispatch = useAppDispatch();
    const mandatoryParams = useMandatoryParams();
    const authUser = useSelector(selectAuthUser);

    // a refused save reloads the record over these, so a stale form cannot be
    // resubmitted on top of someone else's change
    const [record, setRecord] = useState<ApprovalRequestDetail | undefined>(detail);
    const [isSaving, setIsSaving] = useState(false);

    const isCreate = !record;

    /**
     * `Create#self_submission?` -- filing for oneself takes the requester's own
     * path, and that one refuses a request without a justification and at least
     * one levelled competence. Picking someone else leaves both optional, to be
     * completed while the request is worked up.
     */
    const isSelfSubmission = useCallback((userId?: number) =>
        isCreate && (!!own || (!!userId && userId === authUser?.id)), [isCreate, own, authUser?.id]);

    const initialValues: RequestFormValues = useMemo(() => {
        if (!record) {
            return emptyValues;
        }
        return {
            user_id: record.user?.id,
            request_type: record.request_type as ApprovalRequestType,
            subject_title: record.subject_title || '',
            subject_content: record.subject_content || '',
            reason: record.reason || '',
            subject_provider: record.subject_provider || '',
            subject_date: toDateInput(record.subject_date),
            subject_end_date: toDateInput(record.subject_end_date),
            subject_start_time: toTimeInput(record.subject_start_time),
            subject_end_time: toTimeInput(record.subject_end_time),
            subject_location: record.subject_location || '',
            price: (record.price || record.price === 0) ? String(record.price) : '',
            note: record.note || '',
            competences: record.competences?.map(c => ({id: c.id, competence_level: c.competence_level ?? undefined})) || [],
            attachments: record.attachments?.map(f =>
                ({id: f.id, file_name: f.file_name, size: f.size, download_url: f.download_url})) || [],
        };
    }, [record]);

    const handleValidate = useCallback((values: RequestFormValues) => {
        const errors: FormikErrors<RequestFormValues> = {};
        const isCourse = values.request_type === COURSE_MODE;
        // a thematic request is filed without a course, so the realization half
        // is not drawn until someone sources one
        const hasRealization = !isCreate || isCourse;

        if (isCreate && !own && !values.user_id) {
            errors.user_id = t('form.required');
        }

        // a course request is named from the start; a thematic one names the
        // need instead and gains a title only when a course is sourced for it.
        // While creating in topic mode the field is not rendered at all, so a
        // value left over from the course mode must not block the submit on an
        // error nothing can show
        if (!isCreate || isCourse) {
            const title = values.subject_title.trim();
            if (!title) {
                if (isCourse) {
                    errors.subject_title = t('form.required');
                }
            } else if (title.length < MIN_TEXT_LENGTH) {
                errors.subject_title = t('approvals.form.subjectTitleTooShort', {min: MIN_TEXT_LENGTH});
            }
        }

        // a thematic request is defined by its topic, so it has to keep one even
        // after a manager corrects it -- `draft_subject_content_presence`
        if (!isCourse) {
            const topic = values.subject_content.trim();
            if (!topic) {
                errors.subject_content = t('form.required');
            } else if (topic.length < MIN_TEXT_LENGTH) {
                errors.subject_content = t('approvals.form.topicTooShort', {min: MIN_TEXT_LENGTH});
            }
        }

        const competences = values.competences;
        if (isSelfSubmission(values.user_id)) {
            if (!values.reason.trim()) {
                errors.reason = t('form.required');
            }
            // mirrors Create#competences_required_but_missing? and :level_required
            if (!competences.length || competences.some(c => !c.id)) {
                errors.competences = t('approvals.form.competenceRequired') as any;
            } else if (competences.some(c => !c.competence_level)) {
                errors.competences = t('approvals.form.competenceLevelRequired') as any;
            }
        }
        if (!errors.competences && new Set(competences.map(c => c.id)).size !== competences.length) {
            errors.competences = t('approvals.form.competenceDuplicate') as any;
        }

        if (hasRealization) {
            if (values.price.trim() && !isPriceValid(values.price)) {
                errors.price = t('approvals.form.priceInvalid');
            }
            Object.assign(errors, validateRequestTerm(values, t));

            if (values.attachments.length > MAX_FILES) {
                errors.attachments = t('approvals.file.tooMany', {max: MAX_FILES}) as any;
            }
        }
        return errors;
    }, [isCreate, own, isSelfSubmission, t]);

    const handleSubmit = useCallback(async (values: RequestFormValues) => {
        const isCourse = values.request_type === COURSE_MODE;
        if (own && !authUser?.id) {
            // the picker names the student in the manager's form; here it is the
            // signed-in user, and there is nothing to file without them
            return;
        }
        setIsSaving(true);

        const competences = values.competences.map(c => ({id: c.id!, competence_level: c.competence_level}));
        // a thematic request being filed carries neither a course nor anything
        // that describes one -- the term, price, provider, time and place are
        // sourced later, while it is worked up
        const term = (!isCreate || isCourse)
            ? {
                subject_provider: values.subject_provider.trim(),
                subject_date: fromDateInput(values.subject_date) || null,
                subject_end_date: fromDateInput(values.subject_end_date) || null,
                subject_start_time: values.subject_start_time || null,
                subject_end_time: values.subject_end_time || null,
                subject_location: values.subject_location.trim(),
                price: values.price.trim() ? toPrice(values.price) : null,
                note: values.note.trim(),
                competences,
                attachment_file_ids: values.attachments.map(f => f.id),
            }
            // the files ride along even so: the mode can be switched after one is
            // attached, and dropping the ids here would upload it and link it to
            // nothing. The edit and the detail show attachments in either mode
            : {
                note: values.note.trim(),
                competences,
                attachment_file_ids: values.attachments.map(f => f.id),
            };

        const res = record
            // null clears a value the manager emptied; undefined would leave it in
            // place -- which is what a course request needs for the topic, a field
            // it neither carries nor shows
            ? await dispatch(updateApprovalRequest({
                id: String(record.id),
                body: {
                    ...term,
                    subject_title: values.subject_title.trim(),
                    subject_content: isCourse ? undefined : values.subject_content.trim(),
                }
            }))
            : await dispatch(createApprovalRequest({
                body: {
                    ...term,
                    user_id: (own ? authUser?.id : values.user_id)!,
                    request_type: values.request_type,
                    subject_title: isCourse ? values.subject_title.trim() : undefined,
                    subject_content: isCourse ? undefined : values.subject_content.trim(),
                    // seeds the learning request's justification as well
                    reason: values.reason.trim() || undefined,
                }
            }));
        setIsSaving(false);

        if (isApiResultError(res)) {
            // apiErrorMiddleware already surfaces the server's own explanation
            dispatch(addMessage({
                severity: 'error',
                title: t(record ? 'approvals.toast.updateFailed'
                    : (own ? 'approvals.requests.toast.createFailed' : 'approvals.toast.requestCreateFailed'))
            }));
            if (record) {
                // the record is pulled again so the form cannot keep pushing a stale state
                const reloaded = await dispatch(fetchApprovalRequest({id: String(record.id)}));
                if (!isApiResultError(reloaded)) {
                    setRecord(reloaded.payload as ApprovalRequestDetail);
                }
            }
            return;
        }
        dispatch(addMessage({
            severity: 'success',
            title: t(record ? 'approvals.toast.updated'
                : (own ? 'approvals.requests.toast.created' : 'approvals.toast.requestCreated'))
        }));
        const id = record?.id || (res.payload as ApprovalRequestDetail)?.id;
        // the requester's own detail lives under the dashboard, the manager's in the agenda
        const list = own ? '#/dashboard/requests' : '#/approvals';
        window.location.href = id ? list + '/' + id : list;
    }, [record, own, authUser?.id, isCreate, dispatch, t]);

    return <Formik initialValues={initialValues} onSubmit={handleSubmit} validate={handleValidate} enableReinitialize>
        {({handleSubmit: submit, values, errors, touched, submitCount, setFieldValue}) => {
            const showError = (field: keyof RequestFormValues) =>
                (touched[field] || submitCount > 0) ? errors[field] as string : undefined;
            const isCourse = values.request_type === COURSE_MODE;
            const isTopic = !isCourse;
            const isSelf = isSelfSubmission(values.user_id);
            const missing = readinessMissing(values);

            const noteField = <>
                <TextFormField name={'note'} maxlength={1000} label={t('approvals.field.note')}/>
                <Typography component={'div'} sx={hintStyle}>{t('approvals.hint.note')}</Typography>
            </>;

            const competenceField = <CompetenceLevelFormField name={'competences'}
                required={isSelf}
                label={t('approvals.field.competencesWithLevel')}
                error={showError('competences')}/>;

            /** the course that answers the request -- the manager's half either way */
            const realizationFields = <>
                <Grid size={{xs: 12, md: 4}}>{competenceField}</Grid>
                <Grid size={{xs: 6, md: 2}}>
                    <TextFormField name={'subject_date'} type={'date'} shrink
                        minValue={todayYmd()}
                        label={t('approvals.field.termFrom')}/>
                </Grid>
                <Grid size={{xs: 6, md: 2}}>
                    <TextFormField name={'subject_end_date'} type={'date'} shrink
                        minValue={values.subject_date || todayYmd()}
                        label={t('approvals.field.termTo')}/>
                </Grid>
                <Grid size={{xs: 12, md: 4}}>
                    <TextFormField name={'price'} label={t('approvals.field.pricePerStudent')}/>
                </Grid>

                <Grid size={{xs: 12, md: 4}}>
                    <TextFormField name={'subject_provider'} maxlength={255}
                        label={t('approvals.field.provider')}/>
                </Grid>
                <Grid size={{xs: 6, md: 2}}>
                    <TextFormField name={'subject_start_time'} type={'time'} shrink
                        label={t('approvals.field.timeFrom')}/>
                </Grid>
                <Grid size={{xs: 6, md: 2}}>
                    <TextFormField name={'subject_end_time'} type={'time'} shrink
                        label={t('approvals.field.timeTo')}/>
                </Grid>
                <Grid size={{xs: 12, md: 4}}>
                    <TextFormField name={'subject_location'} maxlength={255}
                        label={t('approvals.field.location')}/>
                </Grid>

                <Grid size={{xs: 12, md: 4}}>{noteField}</Grid>
                <Grid size={{xs: 12, md: 8}}>
                    <Stack spacing={1}>
                        <ApprovalFileField
                            label={t('approvals.requests.attachments')}
                            hint={t('approvals.file.draftTypes')}
                            multiple
                            maxFiles={MAX_FILES}
                            acceptedTypes={DRAFT_ACCEPTED_MIME_TYPES}
                            value={values.attachments}
                            error={showError('attachments')}
                            onChange={(files) => setFieldValue('attachments', files)}/>
                        {/* the requester opens the same detail, so nothing here is internal */}
                        <Typography component={'div'} sx={hintStyle}>
                            {t('approvals.form.requestFilesVisible', {max: MAX_FILES})}
                        </Typography>
                    </Stack>
                </Grid>
            </>;

            /** a thematic request being filed: what it is about, and nothing of the course */
            const topicFields = <Grid size={{xs: 12, md: 4}}>
                <Stack spacing={3}>
                    {competenceField}
                    <div>{noteField}</div>
                </Stack>
            </Grid>;

            const createHint = own
                ? (isCourse ? 'approvals.requests.createHint' : 'approvals.requests.createTopicHint')
                : (isCourse ? 'approvals.form.requestCreateHint' : 'approvals.form.requestCreateTopicHint');

            // without noValidate the browser would block the submit with its own
            // transient bubble and the inline messages would never be reached
            return <form onSubmit={submit} noValidate className={'layout-fill layout-column tw-absolute'}>
                <div className={'kws-content-margin flex tw-h-100'}>
                    <Card className={'tw-h-full tw-flex tw-flex-col'}>
                        <CardHeader
                            className={'max-sm:tw-flex-wrap max-sm:tw-gap-12'}
                            title={<>
                                <IconButton onClick={onBack} color={'inherit'} aria-label={t('approvals.action.back')}>
                                    <ArrowBack/>
                                </IconButton>
                                <PlaylistAddCheck/>
                                <span>{t(!isCreate ? 'approvals.form.requestEditTitle'
                                    : (own ? 'approvals.requests.createTitle' : 'approvals.form.requestCreateTitle'))}</span>
                            </>}
                            action={<Button color={'primary'} variant={'contained'} type={'submit'}
                                disabled={isSaving} className={'tw-w-auto'}>
                                {t(own ? 'approvals.requests.submit' : 'form.save')}</Button>}
                        />
                        <CardContent className={'tw-p-32 tw-flex-grow tw-flex tw-flex-col tw-overflow-auto'}>
                            {isCreate
                                ? <>
                                    <RequestModeToggle value={values.request_type}
                                        onChange={(mode) => setFieldValue('request_type', mode)}/>
                                    <Grid container spacing={3}>
                                        {/* one narrow column: who it is for, and what it is about */}
                                        <Grid size={{xs: 12, md: 3}}>
                                            <Stack spacing={3}>
                                                {!own && <ApprovalUserSelectField name={'user_id'}
                                                    label={t('approvals.field.student')}
                                                    placeholder={t('approvals.form.addUser')}/>}
                                                {isCourse
                                                    ? <TextFormField name={'subject_title'} required maxlength={255}
                                                        label={t('approvals.field.courseName')}/>
                                                    // the topic is the whole point of a thematic request
                                                    : <TextFormField name={'subject_content'} required maxlength={255}
                                                        label={t('approvals.field.topic')}/>}
                                            </Stack>
                                        </Grid>
                                        <Grid size={12}>
                                            <TextFormField name={'reason'} type={'textarea'}
                                                required={isSelf}
                                                label={t('approvals.field.justification')}
                                                minRows={1} maxRows={6} maxlength={1000}/>
                                        </Grid>
                                        {isCourse ? realizationFields : topicFields}
                                        <Grid size={12}>
                                            <Typography component={'div'} sx={hintStyle}>{t(createHint)}</Typography>
                                        </Grid>
                                    </Grid>
                                </>
                                : <>
                                    {/* what still stands between this request and a decision */}
                                    <Typography component={'div'} className={'tw-mb-24'} sx={hintStyle}>
                                        {missing.length
                                            ? t('approvals.form.readinessMissing', {fields: missing.map(key => t(key)).join(', ')})
                                            : t('approvals.form.readinessComplete')}
                                    </Typography>

                                    <Typography component={'div'} sx={sectionStyle}>{t('approvals.form.fromRequester')}</Typography>
                                    <Grid container spacing={3} className={'tw-pt-24'}>
                                        <Grid size={{xs: 12, md: 3}}>
                                            <ReadOnlyField label={t('approvals.field.fullName')} value={record?.user?.full_name}/>
                                        </Grid>
                                        {/* only the attributes this space actually collects, like the AngularJS user screens */}
                                        <UserAttributeCell show={mandatoryParams.personal_id}>
                                            <ReadOnlyField label={t('approvals.field.personalId')} value={record?.user?.personal_id}/>
                                        </UserAttributeCell>
                                        <UserAttributeCell show={mandatoryParams.department}>
                                            <ReadOnlyField label={t('approvals.field.department')} value={record?.user?.department}/>
                                        </UserAttributeCell>
                                        <UserAttributeCell show={mandatoryParams.job_title}>
                                            <ReadOnlyField label={t('approvals.field.jobTitle')} value={record?.user?.job_title}/>
                                        </UserAttributeCell>

                                        <Grid size={{xs: 12, md: 3}}>
                                            <ReadOnlyField label={t('approvals.field.mode')}
                                                value={t('approvals.type.' + values.request_type)}/>
                                        </Grid>
                                        {/* what a thematic request is about, as the requester filed it --
                                            a manager may sharpen it while working the request up */}
                                        {isTopic && <Grid size={{xs: 12, md: 9}}>
                                            <TextFormField name={'subject_content'} required maxlength={255}
                                                label={t('approvals.field.topic')}/>
                                        </Grid>}
                                        {/* the course belongs to the manager, but the order of the fields
                                            is the same in every state -- what may be edited is told by the
                                            input's own frame, never by where the field sits */}
                                        <Grid size={12}>
                                            <TextFormField name={'subject_title'} maxlength={255}
                                                required={isCourse}
                                                label={t('approvals.field.courseName')}/>
                                        </Grid>
                                        {/* a whole row: a justification is a sentence or two, not a cell */}
                                        <Grid size={12}>
                                            <ReadOnlyField label={t('approvals.field.justification')}
                                                value={record && approvalJustification(record)}/>
                                        </Grid>
                                    </Grid>

                                    {/* the edit frames draw a rule between the two halves; the
                                        detail, where nothing is owned by anyone, does not */}
                                    <Divider className={'tw-mt-24'}/>
                                    <Typography component={'div'} sx={managerSectionStyle}
                                        className={'tw-mt-12'}>{t('approvals.form.managerPart')}</Typography>
                                    <Grid container spacing={3} className={'tw-pt-24'}>
                                        {realizationFields}
                                    </Grid>
                                </>}
                        </CardContent>
                    </Card>
                </div>
            </form>;
        }}
    </Formik>;
};
